(() => {
  const NAVIGATION_PATH = /^\/(collections|pages)\//;
  const INTERNAL_HOSTS = new Set([
    'ricochetoffroad.com',
    'www.ricochetoffroad.com',
    'ricochet-off-road.myshopify.com'
  ]);

  const normalizeText = (value) => String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

  const filterTerms = (value) => {
    const terms = String(value || '').normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '').toLowerCase().match(/[a-z]+|\d+/g) || [];
    // Keep a spaced model suffix atomic: CRF 250 R X is RX, not R.
    for (let i = 1; i < terms.length; i += 1) {
      if (/^\d+$/.test(terms[i - 1]) && /^[a-z]$/.test(terms[i])) {
        while (/^[a-z]$/.test(terms[i + 1] || '')) terms[i] += terms.splice(i + 1, 1)[0];
      }
    }
    return terms;
  };

  const matchesJoinedWords = (words, term) => {
    if (term.length > 200) return false;
    // Segment only against this link's words; no aliases or cross-link matches.
    const reachable = new Set([0]);
    for (let offset = 0; offset < term.length; offset += 1) {
      if (!reachable.has(offset)) continue;
      const remainder = term.slice(offset);
      for (const word of words) {
        if (term.startsWith(word, offset)) reachable.add(offset + word.length);
        // Complete earlier words, then allow a progressive final word prefix.
        if (offset > 0 && word.startsWith(remainder)) return true;
      }
    }
    return reachable.has(term.length);
  };

  const matchesTerms = (label, query) => query.every((term, index) => {
    // Browse numeric prefixes as the shopper types; never select or rewrite a model.
    if (/^\d+$/.test(term)) {
      return label.terms.some((value) => /^\d+$/.test(value) && value.startsWith(term));
    }
    const previous = query[index - 1];
    const suffixes = label.suffixes.filter((code) => previous && code[1].startsWith(previous));
    if (suffixes.length && term.length <= 3) {
      // An explicit attached suffix still requires the complete number and suffix.
      return suffixes.some((code) => code[1] === previous && code[2] === term);
    }
    if (term.length === 1 && query.length > 1) return label.terms.includes(term);
    // Numeric/suffix guards above remain authoritative for compact word matching.
    return label.letterRuns.some((run) => run.includes(term)) || matchesJoinedWords(label.words, term);
  });

  const ownText = (element) => [...element.childNodes]
    .filter((node) => node.nodeType === 3)
    .map((node) => node.textContent.trim())
    .filter(Boolean)
    .join(' ')
    .replace(/^[\s\-–—:]+|[\s\-–—:]+$/g, '')
    .trim();

  const resolveInternalPath = (anchor) => {
    let url;
    try {
      url = new URL(anchor.href, window.location.origin);
    } catch (_error) {
      return null;
    }

    if (!NAVIGATION_PATH.test(url.pathname)) return null;
    if (url.origin !== window.location.origin && !INTERNAL_HOSTS.has(url.hostname)) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  };

  const isUtilityLink = (anchor) => /replacement\s+hardware/i.test(anchor.textContent || '');

  const cleanLabel = (value) => String(value || '').replace(/^[\s\-–—]+/, '').trim();
  const linkLabel = (anchor) => cleanLabel(anchor.textContent)
    || cleanLabel(anchor.title) || cleanLabel(anchor.querySelector('img')?.alt);

  const uniqueLinks = (links) => {
    const destinations = new Map();
    links.forEach((link) => {
      const destination = resolveInternalPath(link);
      if (!destination || !linkLabel(link)) return;
      const existing = destinations.get(destination);
      if (!existing || (!cleanLabel(existing.textContent) && cleanLabel(link.textContent))) {
        destinations.set(destination, link);
      }
    });
    return [...destinations.values()];
  };

  const makeLink = (source) => {
    const href = resolveInternalPath(source);
    if (!href || !linkLabel(source)) return null;

    const link = document.createElement('a');
    link.className = 'ricochet-navigation-grid__link';
    link.href = href;
    link.textContent = linkLabel(source);
    if (source.title) link.title = source.title;
    return link;
  };

  const groupNameFor = (anchor) => {
    const item = anchor.closest('li');
    let list = item?.parentElement;

    while (list?.matches('ul, ol')) {
      if (list.parentElement?.matches('li')) {
        const label = ownText(list.parentElement);
        if (label) return label;
      }

      const previous = list.previousElementSibling;
      if (previous?.matches('li')) {
        const label = ownText(previous);
        if (label) return label;
      }

      const ancestorList = list.parentElement?.closest('ul, ol');
      if (!ancestorList || ancestorList === list) break;
      list = ancestorList;
    }

    return 'Models';
  };

  const topNavigationLists = (source, links) => {
    const lists = new Set();
    links.forEach((link) => {
      const list = link.closest('ul, ol');
      if (!list) return;
      let top = list;
      while (top.parentElement?.closest('[data-navigation-source]') && top.parentElement.closest('ul, ol')) {
        top = top.parentElement.closest('ul, ol');
      }
      if (source.contains(top)) lists.add(top);
    });
    return [...lists];
  };

  const hideLegacyPrompts = (source) => {
    source.querySelectorAll('p, h2, h3, strong').forEach((element) => {
      const text = element.textContent.replace(/\s+/g, ' ').trim();
      if (/^(choose|select) your (brand|make|model|machine|vehicle)s?[:\s-]*$/i.test(text)) {
        const block = element.closest('p, h2, h3') || element;
        block.remove();
      }
    });
  };

  const renderBrandGrid = (mount, links) => {
    const list = document.createElement('ul');
    list.className = 'ricochet-navigation-grid';
    list.setAttribute('role', 'list');

    links.forEach((sourceLink) => {
      const link = makeLink(sourceLink);
      if (!link) return;
      const item = document.createElement('li');
      item.append(link);
      list.append(item);
    });
    mount.append(list);
  };

  const renderModelGroups = (mount, links) => {
    const groups = new Map();
    links.forEach((link) => {
      const name = groupNameFor(link);
      if (!groups.has(name)) groups.set(name, []);
      groups.get(name).push(link);
    });

    const groupContainer = document.createElement('div');
    groupContainer.className = 'ricochet-navigation-groups';

    groups.forEach((groupLinks, name) => {
      const section = document.createElement('section');
      section.className = 'ricochet-navigation-group';
      section.dataset.navigationGroup = '';

      const heading = document.createElement('h3');
      heading.textContent = name;
      section.append(heading);

      const list = document.createElement('ul');
      list.className = 'ricochet-navigation-grid';
      list.setAttribute('role', 'list');
      groupLinks.forEach((sourceLink) => {
        const link = makeLink(sourceLink);
        if (!link) return;
        const item = document.createElement('li');
        item.append(link);
        list.append(item);
      });
      section.append(list);
      groupContainer.append(section);
    });
    mount.append(groupContainer);
  };

  const wireFilter = (page, input, status) => {
    const items = [...page.querySelectorAll('.ricochet-navigation-grid > li')];
    const labels = items.map((item) => {
      const terms = filterTerms(item.textContent);
      // Only joined catalog codes carry attached suffixes; "4 Turbo EPS" does not.
      const suffixes = (item.textContent.toLowerCase().match(/[a-z0-9]+/g) || [])
        .map((word) => word.match(/(\d+)([a-z]{1,3})$/)).filter(Boolean);
      return { item, terms, suffixes, words: [...new Set(terms.filter((term) => /^[a-z]+$/.test(term)))],
        letterRuns: terms.map((term) => /^\d+$/.test(term) ? ' ' : term).join('').split(' ') };
    });
    const groups = [...page.querySelectorAll('[data-navigation-group]')];
    const empty = page.querySelector('[data-navigation-empty]');
    const clear = page.querySelector('[data-navigation-clear]');

    const update = (event) => {
      const query = filterTerms(globalThis.RicochetSearchNormalizer?.stripModelYears?.(input.value) || input.value);
      let visible = 0;
      labels.forEach((label) => {
        const { item } = label;
        const matches = matchesTerms(label, query);
        item.hidden = !matches;
        if (matches) visible += 1;
      });
      groups.forEach((group) => {
        group.hidden = !group.querySelector('.ricochet-navigation-grid > li:not([hidden])');
      });
      status.textContent = query.length
        ? `${visible} ${visible === 1 ? 'option' : 'options'} found.`
        : `${items.length} options available.`;
      if (empty) empty.hidden = visible > 0;
      if (event?.type === 'input') {
        globalThis.RicochetDiscoveryAnalytics?.navigation(page, {
          query: input.value, total: visible, shown: visible,
          mode: page.querySelector('.ricochet-navigation-grid__link')?.pathname.startsWith('/collections/') ? 'collection' : 'native'
        });
      }
    };

    input.addEventListener('input', update);
    clear?.addEventListener('click', () => {
      input.value = '';
      update();
      input.focus();
    });
    update();
  };

  const enhancePage = (page) => {
    if (page.dataset.navigationReady === 'true') return;
    const source = page.querySelector('[data-navigation-source]');
    const browser = page.querySelector('[data-navigation-browser]');
    const mount = page.querySelector('[data-navigation-grid]');
    const input = page.querySelector('[data-navigation-filter]');
    const status = page.querySelector('[data-navigation-status]');
    if (!source || !browser || !mount || !input || !status) return;

    const allLinks = [...source.querySelectorAll('a')]
      .filter((anchor) => resolveInternalPath(anchor) && !isUtilityLink(anchor));
    const collectionLinks = allLinks.filter((anchor) => resolveInternalPath(anchor)?.startsWith('/collections/'));
    const isModelPage = collectionLinks.length >= 2;
    const sourceLinks = isModelPage ? collectionLinks : allLinks;
    const navigationLinks = uniqueLinks(sourceLinks);
    if (navigationLinks.length < 2) return;

    if (isModelPage) {
      renderModelGroups(mount, navigationLinks);
      sourceLinks
        .filter((link) => !link.closest('ul, ol'))
        .forEach((link) => link.closest('h2, h3, li')?.remove());
      topNavigationLists(source, sourceLinks).forEach((list) => list.remove());
    } else {
      renderBrandGrid(mount, navigationLinks);
      sourceLinks.forEach((link) => link.closest('h2, h3, li')?.remove());
    }

    hideLegacyPrompts(source);
    source.querySelectorAll('p').forEach((paragraph) => {
      if (!paragraph.textContent.trim() && !paragraph.querySelector('img, video, iframe, a, input')) paragraph.remove();
    });
    page.querySelector('[data-navigation-title]').textContent = isModelPage ? 'Select model' : 'Select brand';
    const intro = page.querySelector('[data-navigation-intro]');
    if (intro && isModelPage) intro.textContent = 'Choose your model to see available parts.';
    page.querySelector('[data-navigation-filter-label]').textContent = isModelPage ? 'Filter models' : 'Filter brands';
    input.placeholder = isModelPage ? 'Type your model' : 'Type a brand';
    browser.hidden = false;
    page.querySelector('[data-navigation-help]').hidden = false;
    page.dataset.navigationReady = 'true';
    wireFilter(page, input, status);
  };

  const init = () => document.querySelectorAll('[data-ricochet-navigation-page]').forEach(enhancePage);

  window.RicochetNavigationPage = { init, normalizeText };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
